import { Field, Select } from '@bb/ui';
import { useState, type ReactNode } from 'react';
import { NumberInput } from '../features/campaigns/trigger/NumberInput.tsx';

export type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';
export const DURATION_UNIT_SECONDS: Record<DurationUnit, number> = { seconds: 1, minutes: 60, hours: 3_600, days: 86_400 };

/** The largest of `units` that divides `seconds`, so a stored duration reads as it was entered (`7200` → 2 hours). */
export function durationUnitOf(seconds: number, units: readonly DurationUnit[]): DurationUnit {
  const largestFirst = [...units].sort((a, b) => DURATION_UNIT_SECONDS[b] - DURATION_UNIT_SECONDS[a]);
  return largestFirst.find((u) => seconds > 0 && seconds % DURATION_UNIT_SECONDS[u] === 0) ?? largestFirst.at(-1)!;
}

/** «5 minutes», «28 days», «90 seconds». */
export function formatSeconds(seconds: number): string {
  const unit = durationUnitOf(seconds, ['seconds', 'minutes', 'hours', 'days']);
  const n = seconds / DURATION_UNIT_SECONDS[unit];
  return `${n} ${n === 1 ? unit.slice(0, -1) : unit}`;
}

export interface DurationFieldProps {
  /** Labels the amount; the unit select is «<label> unit». */
  label: string;
  seconds: number;
  /** Called with whole seconds; an empty amount is not a duration and is reported by the field itself. */
  onChange: (seconds: number) => void;
  /** Whether the amount is empty right now (the value keeps the last duration meanwhile). */
  onEmptyChange?: (empty: boolean) => void;
  units: readonly DurationUnit[];
  hint?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
}

/** A duration as an amount and a unit, stored in seconds. */
export function DurationField({ label, seconds, onChange, onEmptyChange, units, hint, error, disabled }: DurationFieldProps) {
  const [unit, setUnit] = useState<DurationUnit>(() => durationUnitOf(seconds, units));
  const [empty, setEmpty] = useState(false);
  const emit = (amount: number | undefined, u: DurationUnit) => {
    const isEmpty = amount === undefined;
    if (isEmpty !== empty) onEmptyChange?.(isEmpty);
    setEmpty(isEmpty);
    if (amount !== undefined) onChange(Math.round(amount * DURATION_UNIT_SECONDS[u]));
  };
  return (
    <div className="duration-field">
      {/* While the amount is empty it stays empty, also when the unit changes. */}
      <Field label={label} hint={hint} error={empty ? 'Enter a duration.' : error}>
        <NumberInput min={0} step={1} value={empty ? undefined : seconds / DURATION_UNIT_SECONDS[unit]} disabled={disabled} onValueChange={(n) => emit(n, unit)} />
      </Field>
      <Field label={`${label} unit`}>
        <Select
          options={units.map((u) => ({ value: u, label: u }))}
          value={unit}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value as DurationUnit;
            setUnit(next);
            if (!empty) emit(seconds / DURATION_UNIT_SECONDS[unit], next);
          }}
        />
      </Field>
    </div>
  );
}
